import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    ItemGroupContextType,
    ItemPartContentProps,
    ItemContextType,
    ItemPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const ITEM_PART_NAMES: Record<ItemPartKind, string> = {
    description: "Description",
    separator: "Separator",
    actions: "Actions",
    content: "Content",
    footer: "Footer",
    header: "Header",
    group: "Group",
    media: "Media",
    title: "Title",
    item: "Item"
};

export const ItemContext = createContext<ItemContextType | undefined>(undefined);

export const ItemGroupContext = createContext<ItemGroupContextType | undefined>(undefined);

export const useItemGroupContext = (): ItemGroupContextType | undefined => {
    return useContext(ItemGroupContext);
};

export const useItemContext = ({
    part
}: {
    part: ItemPartKind;
}): ItemContextType | undefined => {
    const context = useContext(ItemContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Item.${ITEM_PART_NAMES[part]}: must be rendered inside <Item>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withItemContext = <P extends object>(
    part: ItemPartKind,
    Content: ComponentType<ItemPartContentProps<P>>
): FC<P> & {
    itemPart: ItemPartKind;
} => {
    const ItemPartBase: FC<P> = (props) => {
        const context = useItemContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    ItemPartBase.displayName = `Item.${ITEM_PART_NAMES[part]}`;

    return Object.assign(ItemPartBase, {
        itemPart: part
    });
};
